<script setup lang="ts">
import { computed, toRefs } from "vue";
import { getDay, getDaysInMonth, startOfMonth } from "date-fns";
import type {
  IUserSelection,
  IViewData,
} from "@/stories/form/DatePicker/shared";
import ViewHeader from "@/stories/form/DatePicker/ViewHeader.vue";
import { useDates } from "@/stories/utils/useDates";
import { toUnix } from "@/stories/utils/dates";

const CURRENT_YEAR = new Date().getFullYear();

const CURRENT_MONTH = new Date().getMonth();

interface Props {
  view: IViewData;
  selection: IUserSelection | null;
  minDate?: number;
  maxDate?: number;
}

interface ICell {
  day: number;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

const props = defineProps<Props>();

const { selection, view } = toRefs(props);

const isDayDisabled = (year: number, month: number, day: number): boolean => {
  const { minDate, maxDate } = props;
  if (!minDate && !maxDate) return false;

  const dayTimestamp = toUnix(new Date(year, month, day, 23, 59, 59));

  if (minDate && dayTimestamp < minDate) return true;
  if (maxDate && dayTimestamp > maxDate) return true;

  return false;
};

const shortWeekdays = computed(() => {
  const formatter = new Intl.DateTimeFormat(navigator.language || "da", {
    weekday: "short",
  });

  const shortWeekdays = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(2023, 0, 2 + i); // Jan 1, 2023 is a Monday
    shortWeekdays.push(formatter.format(date).toUpperCase());
  }

  return shortWeekdays;
});

const getMatrix = () => {
  const currentDate = new Date(view.value.year, view.value.month, 1);

  // First day of the current date. We use Monday as the first day.
  // That's why we substruct 1.
  let startAt = getDay(startOfMonth(currentDate)) - 1;

  /**
   * The first day of the week can not be -1. Since we substruct the
   * day in the above, we should check it. If it is -1, we should use 6.
   *
   * In date-fns, Sunday is 0, Monday is 6
   * In our component: Sunday is 6, Monday is 0
   *
   * If the value is -1, it should be 6 (Sundan).
   */
  if (startAt === -1) {
    startAt = 6;
  }

  // How many days we have in the month
  const daysInMonth = getDaysInMonth(currentDate);
  // How many days we should show in the calendar at least
  const totalDays = startAt + daysInMonth;
  // How many rows do we need
  const rowCount = totalDays / 7;

  // We need to create the calendar matrix. Let's create an empty one
  const matrix: Array<Array<null | ICell>> = [];

  // Let's assume that the first day of the month starts at 3.
  // We make it a negative value. In the matrix, we add only positive values.
  // So that, previous month's days will not add to the matrix.
  let dayNumber = startAt * -1;

  // If the view is set to show the selected month
  const isModelAndViewMatched =
    view.value.year === selection.value?.year &&
    view.value.month === selection.value?.month;

  // IF the view is set to show the current month
  const isCurrentMonth =
    view.value.year === CURRENT_YEAR && view.value.month === CURRENT_MONTH;

  // The current day of the month
  const currentDay = new Date().getDate();

  // Let's rolling
  for (let row = 0; row < rowCount; row++) {
    // Create a new row to put column values
    matrix[row] = [];

    // We have 7 days
    for (let column = 0; column < 7; column++) {
      // If the day is a previous month's day, or the next month's day,
      // we don't add it to the matrix
      if (++dayNumber > 0 && dayNumber <= daysInMonth) {
        const isToday = isCurrentMonth && dayNumber === currentDay;
        const isSelected =
          isModelAndViewMatched && dayNumber === selection.value?.day;
        const isDisabled = isDayDisabled(
          view.value.year,
          view.value.month,
          dayNumber,
        );

        // Set the number
        matrix[row]![column] = {
          day: dayNumber,
          isToday: isSelected ? false : isToday,
          isSelected,
          isDisabled,
        };
      } else {
        // We should show an empty div to NOT broke the design
        matrix[row]![column] = null;
      }
    }
  }

  return matrix;
};

const isPrevDisabled = computed(() => {
  const { minDate } = props;
  if (!minDate) return false;
  const { year, month } = view.value;
  // Last moment of the last day of the previous month
  const lastDayOfPrevMonth =
    month > 0
      ? toUnix(
          new Date(
            year,
            month - 1,
            getDaysInMonth(new Date(year, month - 1)),
            23,
            59,
            59,
          ),
        )
      : toUnix(
          new Date(
            year - 1,
            11,
            getDaysInMonth(new Date(year - 1, 11)),
            23,
            59,
            59,
          ),
        );
  return lastDayOfPrevMonth < minDate;
});

const isNextDisabled = computed(() => {
  const { maxDate } = props;
  if (!maxDate) return false;
  const { year, month } = view.value;
  // First moment of the first day of the next month
  const firstDayOfNextMonth =
    month < 11
      ? toUnix(new Date(year, month + 1, 1, 0, 0, 0))
      : toUnix(new Date(year + 1, 0, 1, 0, 0, 0));
  return firstDayOfNextMonth > maxDate;
});

const setPrevMonth = () => {
  let { year, month } = view.value;
  if (month > 0) {
    month--;
  } else {
    month = 11;
    year--;
  }
  emit("setView", { year, month });
};

const setNextMonth = () => {
  let { year, month } = view.value;
  if (month < 11) {
    month++;
  } else {
    month = 0;
    year++;
  }
  emit("setView", { year, month });
};

const emit = defineEmits(["setView", "setSelection"]);

const handleClick = (cell: ICell | null) => {
  if (cell && cell.day && !cell.isDisabled) {
    emit("setSelection", {
      year: view.value.year,
      month: view.value.month,
      day: cell.day,
    });
  }
};

const { formatByOptions } = useDates();
</script>

<template>
  <div>
    <ViewHeader
      :prev-disabled="isPrevDisabled"
      :next-disabled="isNextDisabled"
      @prev-click="setPrevMonth"
      @next-click="setNextMonth"
      @title-click="$emit('setView', { type: 'months' })"
    >
      {{
        formatByOptions(new Date(view.year, view.month, 1), {
          month: "long",
          year: "numeric",
        })
      }}
    </ViewHeader>
    <div class="grid grid-cols-7 text-neutral500 mt-1 mb-1 h200">
      <div
        class="w-10 text-center"
        v-for="(code, index) in shortWeekdays"
        :key="index"
      >
        {{ code }}
      </div>
    </div>

    <div class="flex flex-col gap-0">
      <div v-for="(row, rowIndex) in getMatrix()" :key="rowIndex">
        <div class="grid grid-cols-7">
          <button
            type="button"
            v-for="(cell, cellIndex) in row"
            :key="cellIndex"
            :class="[
              'text-center p-1 text-base text-neutral1000 rounded-lg transition w-10 h-10 focus-visible-strong',
              cell?.isToday ? 'bg-neutral50 text-darkOcean500 font-bold' : '',
              cell?.isSelected
                ? 'bg-darkOcean100 border border-darkOcean500 focus-visible:border-none'
                : '',
              'disabled:opacity-40 disabled:text-neutral600',
            ]"
            :disabled="cell?.isDisabled"
            :tabindex="cell?.day === undefined || cell?.isDisabled ? -1 : 0"
            @click="() => handleClick(cell)"
          >
            {{ cell?.day }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
